/**
 * Variables Display Component
 * Read-only display of variables that can be used in prompts.
 * Uses shadcn theme tokens for dark/light mode support.
 */

import React from 'react'
import { Shield, Search, Database } from 'lucide-react'

interface VariableConfig {
  default?: string
  required?: boolean
  auto_filter?: boolean
  auto_train?: boolean
}

type VariableValue = string | VariableConfig

interface VariablesDisplayProps {
  formData: any
}

const getDisplayValue = (value: VariableValue): string =>
  typeof value === 'string' ? value : value.default || ''

const getFlags = (value: VariableValue) =>
  typeof value === 'string'
    ? { required: false, auto_filter: false, auto_train: false }
    : { required: value.required || false, auto_filter: value.auto_filter || false, auto_train: value.auto_train || false }

export const VariablesDisplay: React.FC<VariablesDisplayProps> = ({ formData }) => {
  const variables: Record<string, VariableValue> = (formData.settings?.variables as any) || {}
  const entries = Object.entries(variables)

  if (entries.length === 0) return null

  return (
    <div className="rounded-lg border bg-muted/40 p-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        Available variables — use <span className="font-mono">{`{{name}}`}</span> in your prompt
      </p>
      <div className="flex flex-wrap gap-1.5">
        {entries.map(([name, value]) => {
          const displayValue = getDisplayValue(value)
          const flags = getFlags(value)
          return (
            <span
              key={name}
              className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs"
            >
              <span className="font-mono font-medium text-foreground">{`{{${name}}}`}</span>
              {displayValue && (
                <span className="text-muted-foreground">= {displayValue}</span>
              )}
              {flags.required && <Shield className="w-3 h-3 text-red-500 ml-0.5" title="Required" />}
              {flags.auto_filter && <Search className="w-3 h-3 text-purple-500" title="Auto Filter" />}
              {flags.auto_train && <Database className="w-3 h-3 text-green-500" title="Auto Train" />}
            </span>
          )
        })}
      </div>
    </div>
  )
}
